import { EdgeItemType } from 'data/types';
import { OpeningEdgeType, OpeningSpanType } from 'utils/opening-position-types';
import { unitVector } from 'utils/opening-visuals-helpers';

export const WINDOW_BAR_COLOR = '#38BDF8';
export const WINDOW_TICK_COLOR = '#0EA5E9';
export const DOOR_BAR_COLOR = '#B45309';
export const OPENING_BAR_THICKNESS_PX = 10;
export const WINDOW_TICK_LENGTH_PX = 6;
export const GARAGE_DOOR_MIN_WIDTH_FT = 8;
export const OPENING_DROP_PREVIEW_OPACITY = 0.4;

export function usesWindowOpeningVisuals(type: EdgeItemType['type']): boolean {
  return type === 'window' || type === 'glass-door';
}

export function openingBarColor(type: EdgeItemType['type']): string {
  return usesWindowOpeningVisuals(type) ? WINDOW_BAR_COLOR : DOOR_BAR_COLOR;
}

export function isGarageDoorWidth(widthFt: number): boolean {
  return widthFt >= GARAGE_DOOR_MIN_WIDTH_FT;
}

export type TickLineType = {
  p1: { x: number; y: number };
  p2: { x: number; y: number };
};

export function inwardNormal(edge: OpeningEdgeType): { x: number; y: number } {
  if (edge === 'top') {
    return { x: 0, y: 1 };
  }
  if (edge === 'bottom') {
    return { x: 0, y: -1 };
  }
  if (edge === 'left') {
    return { x: 1, y: 0 };
  }
  return { x: -1, y: 0 };
}

export function getWindowTickLinesAlongSpan(
  p1: { x: number; y: number },
  p2: { x: number; y: number },
  inward: { x: number; y: number },
  tickLengthPx: number
): TickLineType[] {
  const normal = unitVector(inward);
  if (!normal) {
    return [];
  }
  return [p1, p2].map((point) => ({
    p1: {
      x: point.x - normal.x * tickLengthPx,
      y: point.y - normal.y * tickLengthPx
    },
    p2: {
      x: point.x + normal.x * tickLengthPx,
      y: point.y + normal.y * tickLengthPx
    }
  }));
}

export function getDoorSwingArcPathAlongSpan(
  p1: { x: number; y: number },
  p2: { x: number; y: number },
  inward: { x: number; y: number }
): string {
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  const radius = Math.hypot(dx, dy);
  if (radius === 0) {
    return '';
  }

  const normal = unitVector(inward);
  if (!normal) {
    return '';
  }

  const leaf = {
    x: p1.x + normal.x * radius,
    y: p1.y + normal.y * radius
  };
  const along = { x: dx / radius, y: dy / radius };
  const sweep = normal.x * along.y - normal.y * along.x > 0 ? 1 : 0;

  return `M ${p1.x} ${p1.y} L ${leaf.x} ${leaf.y} A ${radius} ${radius} 0 0 ${sweep} ${p2.x} ${p2.y}`;
}

export function getWindowTickLines(
  span: OpeningSpanType,
  edge: OpeningEdgeType,
  tickLengthPx: number,
  feetToPx: number
): TickLineType[] {
  return getWindowTickLinesAlongSpan(
    { x: span.p1.x * feetToPx, y: span.p1.y * feetToPx },
    { x: span.p2.x * feetToPx, y: span.p2.y * feetToPx },
    inwardNormal(edge),
    tickLengthPx
  );
}

export function getDoorSwingArcPath(
  span: OpeningSpanType,
  edge: OpeningEdgeType,
  feetToPx: number
): string {
  return getDoorSwingArcPathAlongSpan(
    { x: span.p1.x * feetToPx, y: span.p1.y * feetToPx },
    { x: span.p2.x * feetToPx, y: span.p2.y * feetToPx },
    inwardNormal(edge)
  );
}
